import { FC } from 'react';
import type { MentionableUser } from './use-mention-autocomplete';
import styles from './mention-autocomplete.module.scss';

interface MentionListProps {
  results: MentionableUser[];
  /** True when the last lookup failed, as against finding nobody. */
  lookupFailed?: boolean;
  highlighted: number;
  onHighlight: (index: number) => void;
  onSelect: (user: MentionableUser) => void;
}

/**
 * The @-mention dropdown.
 *
 * A listbox rather than a Carbon ComboBox: a ComboBox owns its own input, and this one has
 * to sit under a textarea the person is writing prose in. The ARIA roles are here so a
 * screen reader still announces it as a list of options being filtered, which is what it
 * behaves like.
 *
 * The name is the whole option. It used to carry the address underneath as a
 * disambiguator, which was answering a question Query::Users::Mentionable had already
 * answered: accounts sharing a display name are collapsed to one option there, so no two
 * rows here can be the same person twice. What the address did instead was turn a
 * two-character prefix into a staff directory with contact details, for any signed-in user,
 * against an endpoint nothing rate-limits.
 *
 * Blue, and the same blue as the pill the name gets in the composer, so the list looks like
 * what it produces.
 *
 * Rendering nothing for an empty list is right for "nobody is called that" and wrong for
 * "the lookup failed", which used to look identical: the reader concludes their colleague
 * cannot be mentioned, types the name by hand, and tags nobody. Same shape as the activity
 * feed rendering a failed load as "No activity recorded yet." — so it gets the same answer,
 * a line that says the search did not run.
 *
 * @return {JSX.Element | null} The dropdown, or nothing when there is nothing to offer.
 */
export const MentionList: FC<MentionListProps> = ({
  results,
  highlighted,
  onHighlight,
  onSelect,
  lookupFailed = false,
}) => {
  if (lookupFailed && results.length === 0) {
    return (
      <p className={styles.lookupFailed} role="status">
        Could not look up people just now. Keep typing to try again.
      </p>
    );
  }

  if (results.length === 0) return null;

  return (
    <ul
      className={styles.list}
      role="listbox"
      aria-label="People you can mention"
    >
      {results.map((user, index) => (
        <li key={user.id}>
          <button
            type="button"
            role="option"
            aria-selected={index === highlighted}
            className={
              index === highlighted
                ? `${styles.option} ${styles.optionHighlighted}`
                : styles.option
            }
            // mouseDown, not click: the textarea loses focus first on a click, and the
            // blur handler closes the dropdown before the selection ever lands.
            onMouseDown={(event) => {
              event.preventDefault();
              onSelect(user);
            }}
            onMouseEnter={() => onHighlight(index)}
          >
            <span className={styles.optionName}>
              <span className={styles.optionAt}>@</span>
              {user.name}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
};
